import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, ButtonIcon, CardV2, MenuList } from 'pepsico-ds';
import { urlConstants } from '../../constants/urlConstants';
import { appConstants } from '../../constants/appconstants';
import { getUserClaims, getAuthHeader } from '../../utils/auth/unified-auth';
import { MISessionContext } from './MISessionContext';
import './MarketIntelligenceChat.scss';
import { normalizeSessionHistory,
  getSpendAnalyticsCardVisibilityFromEnv, } from './chatUtils';
import { MI_SUGGESTED_PROMPTS as SUGGESTED_PROMPTS, PROMPTS_PER_PAGE } from './chatConstants';
import { useDispatch } from 'react-redux';
import { FetchUserInfoAction } from '../../redux/action/LandingPageAction';


const { genAITermsUrl, privacyNoticeUrl } = urlConstants;

const MarketIntelligenceChat = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);
  const [promptPage, setPromptPage] = useState(0);
  const [slideDirection, setSlideDirection] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showAgentMenu, setShowAgentMenu] = useState(false);

  const sessionContext = useContext(MISessionContext);
  const dispatch = useDispatch();
  const [userInfo, setUserInfo] = useState({});
  const [isUserDataFetched, setIsUserDataFetched] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [deleteTargetSessionId, setDeleteTargetSessionId] = useState(null);
  const [isDeletingSession, setIsDeletingSession] = useState(false);

  const displaySessionHistory =
    sessionContext?.sessionHistory?.length > 0
      ? sessionContext.sessionHistory
      : sessionHistory;

  const totalPromptPages = Math.ceil(SUGGESTED_PROMPTS.length / PROMPTS_PER_PAGE);
  const visiblePrompts = SUGGESTED_PROMPTS.slice(promptPage * PROMPTS_PER_PAGE, (promptPage + 1) * PROMPTS_PER_PAGE);

  /* istanbul ignore next */
  const handlePromptNav = (direction) => {
    if (isAnimating) return;
    const nextPage = direction === 'left' ? promptPage - 1 : promptPage + 1;
    if (nextPage < 0 || nextPage >= totalPromptPages) return;
    setSlideDirection(direction === 'left' ? 'slide-right' : 'slide-left');
    setIsAnimating(true);
    setTimeout(() => {
      setPromptPage(nextPage);
      setSlideDirection(direction === 'left' ? 'slide-in-from-left' : 'slide-in-from-right');
      setTimeout(() => {
        setSlideDirection('');
        setIsAnimating(false);
      }, 300);
    }, 300);
  };

  /* istanbul ignore next */
  useEffect(() => {
    async function fetchUserInfo() {
      const user = await getUserClaims();
      if (user) {
        setUserInfo(user);
        localStorage.setItem('gpid', btoa(JSON.stringify(user?.gpid)));
        dispatch(FetchUserInfoAction(user));
        setIsUserDataFetched(true);
      }
    }
    setIsDataLoading(true);
    fetchUserInfo();
  }, []);

  /* istanbul ignore next */
  useEffect(() => {
    return () => {
      dispatch({
        type: 'RESET_USER_INFO_ACTION',
        payload: 'RESET_USER_INFO_ACTION',
      });
    };
  }, []);

  useEffect(() => {
    if (showSidebar) {
      fetchSessionHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidebar]);

  const fetchSessionHistory = async () => {
    setIsHistoryLoading(true);
    setHistoryError('');
    try {
      const { Authorization } = await getAuthHeader();
      const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
      const res = await fetch(
        `${appConstants.MI_AI_BASE_URL}/api/v1/chats?chatbot=mi`,
        {
          method: 'GET',
          headers: { Authorization, 'Content-Type': 'application/json' },
        },
      );
      if (!res.ok) {
        throw new Error(`Session history failed: ${res.status}`);
      }
      const data = await res.json();
      const normalized = normalizeSessionHistory(data);
      setSessionHistory(normalized);
      if (sessionContext?.setSessionHistory) {
        sessionContext.setSessionHistory(normalized);
      }
    } catch (err) {
      console.error('Session history error:', err);
      setSessionHistory([]);
      setHistoryError('Unable to load sessions.');
    } finally {
      setIsHistoryLoading(false);
    }
  };

  // const handleConfirmDeleteSession = async () => {
  //   if (!deleteTargetSessionId) return;
  //   setIsDeletingSession(true);
  //   setHistoryError('');

  //   try {
  //     const { Authorization } = getOktaAuthHeader();
  //     const res = await fetch(
  //       `${appConstants.MI_AI_BASE_URL}/api/v1/sessions/${deleteTargetSessionId}`,
  //       {
  //         method: 'DELETE',
  //         headers: { Authorization },
  //       },
  //     );
  //     if (!res.ok) {
  //       throw new Error(`Delete session failed: ${res.status}`);
  //     }

  //     const updateFn = (prev) =>
  //       prev.filter((entry) => entry.sessionId !== deleteTargetSessionId);

  //     setSessionHistory(updateFn);
  //     if (sessionContext?.sessionHistory) {
  //       const updated = updateFn([...sessionContext.sessionHistory]);
  //       if (sessionContext?.setSessionHistory) {
  //         sessionContext.setSessionHistory(updated);
  //       }
  //     }

  //     setDeleteTargetSessionId(null);
  //   } catch (err) {
  //     console.error('Unable to delete session:', err);
  //     setHistoryError('Unable to delete session.');
  //   } finally {
  //     setIsDeletingSession(false);
  //   }
  // };

  const handleSendMessage = () => {
    if (message.trim()) {
      navigate('/ai-assistant/market-intelligence-conversations', {
        state: { message: message.trim() },
      });
    }
  };

  const handlePromptSend = (prompt) => {
    navigate('/ai-assistant/market-intelligence-conversations', {
      state: { message: prompt },
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="mi-chat-container">
      <div className="mi-subheader-2">
        <div className="mi-subheader-left">
          <button className="mi-back-btn" onClick={() => navigate("/ai-assistant")} >
            <Icon icon="arrow_back" style={{ color: '#006FCF' }} />
          </button>
          {// hasMessages && 
          (
            <button className="mi-sidebar-toggle-btn" onClick={() => setShowSidebar(!showSidebar)} style={{ marginLeft: '6px', color: '#006FCF' }}>
              <Icon icon="view_sidebar" style={{ color: '#006FCF' }} />
            </button>
          )}
          <div className="mi-agent-badge" style={{ marginLeft: '6px' }}>MI</div>
          <span
            className="mi-agent-name"
            style={{ marginLeft: '8px', cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={() => setShowAgentMenu(prev => !prev)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowAgentMenu(prev => !prev); }}
          >Market Intelligence</span>
          <Icon icon="arrow_drop_down" style={{ cursor: 'pointer' }} onClick={() => setShowAgentMenu(prev => !prev)} />
          
        </div>
        
        <div className="mi-subheader-right">
        </div>
        
      </div>

      {showAgentMenu && (
      <MenuList
        className="ph-agent-select"
        customNoResultMessage=""
        emptyStateMessage=""
        height={240}
        hideMenuListOnSelection
        isClearable
        items={[
          {
            
            id: '/ai-assistant/knowledge-management',
            label: 'Knowledge Management',
          },
          ...(getSpendAnalyticsCardVisibilityFromEnv() ? 
          [
            {
              label: 'Spend Analytics',
              id: '/spend-analytics-ai-agent',
            }
          ] 
          : []),
        ]}
        listHeaderLabel=""
        onHideMenuList={() => setShowAgentMenu(false)}
        selectedItemCallback={
          function S8e(item)
            { 
              if (item?.[0]?.id) {
                  navigate(item?.[0].id); 
              } 
          }
        }
        selectedItems={[]}
        size="medium"
        style={{}}
        type="single"
        width=""
        showSearch={false}
      />
      )}

      <div className="mi-chat-body">
        {showSidebar && (
          <div className="mi-chat-sidebar">
            <div className="mi-chats-header">
              <span>Chats</span>
              <button className="mi-close-sidebar" onClick={() => setShowSidebar(false)}>
                <Icon icon="close" />
              </button>
            </div>
            <button className="mi-new-chat-btn">
              <span className="mi-new-chat-plus">+</span>
              <span>New chat</span>
            </button>
            <div className="mi-recents">
              <div className="mi-recents-label">Recents</div>
              {isHistoryLoading && (
                <div className="mi-recent-item">Loading sessions...</div>
              )}
              {!isHistoryLoading && historyError && (
                <div className="mi-recent-item">{historyError}</div>
              )}
              {!isHistoryLoading &&
                !historyError &&
                displaySessionHistory.length === 0 && (
                  <div className="mi-recent-item">No sessions</div>
                )}
              {!isHistoryLoading &&
                !historyError &&
                displaySessionHistory.map((chat) => (
                  <div key={chat.sessionId} className="sa-conv-recent-row">
                    <button
                      className="mi-recent-item sa-conv-recent-item"
                      onClick={() =>
                        navigate('/ai-assistant/market-intelligence-conversations', {
                          state: {
                            session_id: chat.sessionId,
                            conversation_id: chat.conversationId,
                          },
                        })
                      }
                      title={chat.title}
                    >
                      <div className="sa-conv-recent-title-wrap" style={{ width: '100%', overflow: 'hidden' }}>
                        <span className="sa-conv-recent-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>
                          {chat.title}
                        </span>
                      </div>
                      {/* {chat.isPinned && (
                        <span className="sa-conv-recent-pinned-badge">
                          Pinned
                        </span>
                      )} */}
                    </button>

                    {/* <button
                      type="button"
                      className="sa-conv-recent-actions-trigger"
                      aria-label="Session actions"
                      title="Session actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRecentMenuOpenId((prev) =>
                          prev === chat.sessionId ? null : chat.sessionId,
                        );
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M9 3.25C9.69036 3.25 10.25 2.69036 10.25 2C10.25 1.30964 9.69036 0.75 9 0.75C8.30964 0.75 7.75 1.30964 7.75 2C7.75 2.69036 8.30964 3.25 9 3.25Z"
                          fill="#4B4B4B"
                        />
                        <path
                          d="M9 10.25C9.69036 10.25 10.25 9.69036 10.25 9C10.25 8.30964 9.69036 7.75 9 7.75C8.30964 7.75 7.75 8.30964 7.75 9C7.75 9.69036 8.30964 10.25 9 10.25Z"
                          fill="#4B4B4B"
                        />
                        <path
                          d="M9 17.25C9.69036 17.25 10.25 16.6904 10.25 16C10.25 15.3096 9.69036 14.75 9 14.75C8.30964 14.75 7.75 15.3096 7.75 16C7.75 16.6904 8.30964 17.25 9 17.25Z"
                          fill="#4B4B4B"
                        />
                      </svg>
                    </button> */}

                    {/* {recentMenuOpenId === chat.sessionId && (
                      <div className="sa-conv-recent-actions-menu" role="menu">
                        <button
                          type="button"
                          className="sa-conv-recent-actions-item"
                          disabled={Boolean(pinLoadingById[chat.sessionId])}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePinSession(chat);
                          }}
                        >
                          {pinLoadingById[chat.sessionId]
                            ? 'Updating...'
                            : chat.isPinned
                              ? 'Unpin'
                              : 'Pin'}
                        </button>
                        <button
                          type="button"
                          className="sa-conv-recent-actions-item sa-conv-recent-actions-item--danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTargetSessionId(chat.sessionId);
                            closeRecentMenu();
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )} */}
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className={`mi-chat-main`}>
          <>
              <div className="mi-chat-welcome">
                <div className="mi-welcome-icon">MI</div>
                <h1 className="mi-welcome-title">Welcome to Market Intelligence. How can I help?</h1>
                <p className="mi-welcome-subtitle">
                  <span style={{ fontWeight: '700', textTransform: 'uppercase' }}>Sources covered</span>: Fastmarket RISI (price, forecast, and news)
                </p>
              </div>

              <div className="mi-chat-input-area">
                <div className="mi-input-wrapper">
                  <input
                    type="text"
                    className="mi-input"
                    placeholder="Send a message to Market Intelligence AI Assistant."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                  <Button
                    variant="primary"
                    size="medium"
                    iconTrailing="send"
                    onClick={handleSendMessage}
                    disabled={!message.trim()}
                    className="mi-send-btn"
                  />
                </div>
                <p className="mi-input-disclaimer">
                  By messaging the AI assistant, you confirm that you have read and agree to our{' '}
                  <a href={genAITermsUrl} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href={privacyNoticeUrl} target="_blank" rel="noopener noreferrer">Privacy Notice</a>.
                  Please do not share sensitive information. Chats may be reviewed and used to improve our models. AI can make mistakes, so please verify important information.
                </p>
                <div className="mi-suggested-prompts">
                  {/* <button className="mi-prompt-nav"><Icon icon="chevron_left" /></button> */}
                  <ButtonIcon
                      className="mi-prompt-nav"
                      color="Interaction/Active"
                      icon={"chevron_left"}
                      label={""}
                      onClick={() => handlePromptNav('left')}
                      size="medium"
                      style={{ opacity: promptPage === 0 ? 0.4 : 1 }}
                      type="default"
                      disabled={promptPage === 0 || isAnimating}
                  />
                  <div className={`mi-prompt-cards-wrapper ${slideDirection}`}>
                    {visiblePrompts.map((prompt, idx) => (
                      <CardV2
                        key={promptPage * PROMPTS_PER_PAGE + idx}
                        className="mi-prompt-card"
                        state="interactive"
                        type="basic"
                        cardBody={{
                          type: "slot",
                          slot: (
                            <div
                              role="button"
                              tabIndex={0}
                              style={{ cursor: 'pointer' }}
                              onClick={() => handlePromptSend(prompt)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePromptSend(prompt); }}
                            >
                              {prompt}
                            </div>
                          )
                        }}
                        onClick={() => handlePromptSend(prompt)}
                      />
                    ))}
                  </div>
                  <ButtonIcon
                      className="mi-prompt-nav"
                      color="Interaction/Active"
                      icon={"chevron_right"}
                      label={""}
                      onClick={() => handlePromptNav('right')}
                      size="medium"
                      style={{ opacity: promptPage >= totalPromptPages - 1 ? 0.4 : 1 }}
                      type="default"
                      disabled={promptPage >= totalPromptPages - 1 || isAnimating}
                  />
                </div>
              </div>
            </>
        </div>
      </div>

      {/* {deleteTargetSessionId && (
        <div className="sa-conv-delete-modal-backdrop" role="presentation">
          <div
            className="sa-conv-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Delete chat"
          >
            <h3>Delete chat?</h3>
            <p>
              Are you sure you want to delete this chat? This action cannot be
              undone.
            </p>
            <div className="sa-conv-delete-modal-actions">
              <button
                type="button"
                onClick={() => setDeleteTargetSessionId(null)}
                disabled={isDeletingSession}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sa-conv-delete-confirm-btn"
                onClick={handleConfirmDeleteSession}
                disabled={isDeletingSession}
              >
                {isDeletingSession ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default MarketIntelligenceChat;
